import { create } from 'zustand';

import { type AnalyticsAdapter } from '@/adapters/analytics/types';
import { type AppStateAdapter, type AppStateStatus } from '@/adapters/appState/types';
import {
  isNotificationPermissionDeniedError,
  isStartCancelledError,
} from '@/adapters/audio/errors';
import { type AudioRecorderAdapter } from '@/adapters/audio/types';
import { type RecordingLiveSurfaceAdapter } from '@/adapters/recordingLiveSurface/types';
import { type VoiceRecordingRepository } from '@/adapters/database/types';
import {
  type CreateMeetingInput,
  type FileTransferAdapter,
  type MeetingRecordingInput,
} from '@/adapters/fileTransfer/types';
import { type LocationProviderAdapter } from '@/adapters/location/types';
import { type Logger } from '@/adapters/logger/types';
import { type NetworkAdapter } from '@/adapters/network/types';
import { hasGraphQLErrorCode } from '@/adapters/graphql/errorCode';
import { ERROR_CODES } from '@/adapters/graphql/errorCodes';
import { type StorageAdapter } from '@/adapters/storage/createStorage';
import { type FileStorageAdapter } from '@/adapters/storage/types';
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from '@/constants/analytics';
import { type UploadStatus, type VoiceRecording } from '@/models/voiceRecording';

export interface AlertInfo {
  text: string;
  variant: 'error' | 'success';
}

export interface VoiceRecordingConfig {
  maxRecordingDurationMs: number;
  startTimeoutMs?: number;
  lowStorageThresholdBytes?: number;
  lowStorageWarningThresholdBytes?: number;
  lowStoragePreFlightThresholdBytes?: number;
  storageSampleIntervalMs?: number;
}

export type RecordingStopTrigger = 'user' | 'duration_limit' | 'low_storage';

export type RecordingStoppedNotificationReason = 'app_killed' | 'low_storage';

const DEFAULT_LOW_STORAGE_THRESHOLD_BYTES = 10 * 1024 * 1024;
const DEFAULT_LOW_STORAGE_WARNING_THRESHOLD_BYTES = 50 * 1024 * 1024;
const DEFAULT_LOW_STORAGE_PRE_FLIGHT_THRESHOLD_BYTES = 200 * 1024 * 1024;
const DEFAULT_STORAGE_SAMPLE_INTERVAL_MS = 10_000;

export interface VoiceRecordingAdapters {
  audioRecorder: AudioRecorderAdapter;
  recordingLiveSurface: RecordingLiveSurfaceAdapter;
  appState: AppStateAdapter;
  locationProvider: LocationProviderAdapter;
  repository: VoiceRecordingRepository;
  fileStorage: FileStorageAdapter;
  generateId: () => string;
  logger: Logger;
  analytics: AnalyticsAdapter;
  storage: StorageAdapter;
  fileTransferAdapter: FileTransferAdapter;
  networkAdapter: NetworkAdapter;
  canUpload: () => Promise<boolean>;
  hasAiTranscriptionConsent: () => Promise<boolean>;
  hasAnyNetwork: () => Promise<boolean>;
  notifyUploadPending: (meetingId: string) => Promise<void>;
  notifyRecordingStopped: (reason: RecordingStoppedNotificationReason) => Promise<void>;
  config: VoiceRecordingConfig;
}

export type RecordingStatus = 'idle' | 'starting' | 'recording' | 'recording_paused' | 'stopping';

export interface RecordingState {
  status: RecordingStatus;
  recordingSessionId: string | null;
  contactId: number | null;
  meetingId: string | null;
  userId: string | null;
  accountId: string | null;
  startedAt: number | null;
  elapsedMs: number;
  segmentStartedAt: number | null;
  items: VoiceRecording[];
}

export interface UploadState {
  isRetrying: boolean;
  retryingRecordingId: string | null;
  completedAt: number | null;
  totalSize: number;
  pendingUploadMeetingIds: string[];
  failedUploadMeetingIds: string[];
}

export interface VoiceRecordingState {
  recording: RecordingState;
  upload: UploadState;
  alert: AlertInfo | null;
  meteringLevel: number;
  lowStorageWarning: boolean;
}

export interface VoiceRecordingStartCallbacks {
  onAutoStopped?: (meetingId: string | null) => void;
}

export interface VoiceRecordingActions {
  requestMicrophonePermission: () => Promise<boolean>;
  startRecording: (
    contactId: number,
    userId: string,
    accountId: string,
    callbacks?: VoiceRecordingStartCallbacks,
  ) => Promise<void>;
  startStandaloneRecording: (
    userId: string,
    accountId: string,
    callbacks?: VoiceRecordingStartCallbacks,
  ) => Promise<void>;
  startRecordingForMeeting: (
    meetingId: string,
    userId: string,
    accountId: string,
    callbacks?: VoiceRecordingStartCallbacks,
  ) => Promise<void>;
  stopRecording: (trigger?: RecordingStopTrigger) => Promise<string | null>;
  cancelRecording: () => Promise<void>;
  reset: () => Promise<void>;
  replayAppKilledMarker: () => Promise<void>;
  loadRecordings: (contactId: number, accountId: string) => Promise<void>;
  loadAllRecordings: (accountId: string) => Promise<void>;
  assignContactsAndUpload: (recordingId: string, contactIds: number[]) => Promise<boolean>;
  deleteRecording: (id: string) => Promise<boolean>;
  pauseRecording: () => Promise<void>;
  resumeRecording: () => Promise<void>;
  dismissLowStorageWarning: () => void;
  shouldConfirmLowStorageStart: () => Promise<boolean>;
  retryPendingUploads: () => Promise<void>;
  retryUpload: (id: string) => Promise<boolean>;
  loadTotalSize: () => Promise<void>;
  loadPendingUploadMeetingIds: () => Promise<void>;
}

const initialRecording: RecordingState = {
  status: 'idle',
  recordingSessionId: null,
  contactId: null,
  meetingId: null,
  userId: null,
  accountId: null,
  startedAt: null,
  elapsedMs: 0,
  segmentStartedAt: null,
  items: [],
};

const APP_KILLED_MARKER_KEY = 'voice_recording_app_killed_marker';
const LIVE_SURFACE_UPDATE_INTERVAL_MS = 500;

const RETRYABLE_UPLOAD_STATUSES = new Set<UploadStatus>(['failed', 'uploading']);

const initialUpload: UploadState = {
  isRetrying: false,
  retryingRecordingId: null,
  completedAt: null,
  totalSize: 0,
  pendingUploadMeetingIds: [],
  failedUploadMeetingIds: [],
};

export const createVoiceRecordingStore = (adapters: VoiceRecordingAdapters) => {
  const { config } = adapters;
  const timer = { ref: null as ReturnType<typeof setInterval> | null };
  const liveSurfaceTimer = { ref: null as ReturnType<typeof setInterval> | null };
  const appStateUnsubscribe = { ref: null as (() => void) | null };

  const clearTimer = () => {
    if (timer.ref) {
      clearInterval(timer.ref);
      timer.ref = null;
    }
  };

  const clearLiveSurfaceTimer = () => {
    if (liveSurfaceTimer.ref) {
      clearInterval(liveSurfaceTimer.ref);
      liveSurfaceTimer.ref = null;
    }
  };

  const clearAppStateListener = () => {
    appStateUnsubscribe.ref?.();
    appStateUnsubscribe.ref = null;
  };

  // findPendingUploads reclaims 'uploading' rows once they pass the staleness
  // window, which exists to recover uploads orphaned by process death. Within a
  // live process we know what is still running, so those must not be reclaimed.
  const inFlightUploads = new Set<string>();

  // The upload helpers are defined outside the store factory, so they reach
  // state through this rather than closing over `set` directly.
  const storeApi = { setState: null as ((partial: Partial<VoiceRecordingState>) => void) | null };

  const withInFlightUpload = async <T>(id: string, run: () => Promise<T>): Promise<T> => {
    inFlightUploads.add(id);

    try {
      return await run();
    } finally {
      inFlightUploads.delete(id);
    }
  };

  const clearRecordingLifecycle = () => {
    clearTimer();
    clearLiveSurfaceTimer();
    clearAppStateListener();
  };

  // Tick-count gating on the shared 1-second lifecycle timer: the storage
  // read runs every Nth tick. Counting ticks instead of reading the wall
  // clock keeps sampling deterministic under fake-timer test runtimes and is
  // unaffected by clock jumps on device.
  const LIFECYCLE_TICK_MS = 1000;
  const storageMonitor = {
    ticksUntilNextSample: 0,
    availableBytes: null as number | null,
  };

  // Latest app-state event while a session is active; used to keep the
  // warning banner foreground-only and to notify on backgrounded stops.
  const currentAppState = { value: 'active' as AppStateStatus };

  const endLiveSurface = () => {
    void adapters.recordingLiveSurface.end().catch(adapters.logger.captureException);
  };

  const getContactIds = (recording: VoiceRecording, overrideContactIds?: number[]) =>
    overrideContactIds ?? (recording.contactId === null ? [] : [recording.contactId]);

  const getLocation = (recording: VoiceRecording) =>
    recording.latitude !== null && recording.longitude !== null
      ? {
          latitude: recording.latitude,
          longitude: recording.longitude,
          address: recording.address ?? '',
        }
      : undefined;

  const getDurationSeconds = (recording: VoiceRecording) =>
    recording.durationMs ? Math.round(recording.durationMs / 1000) : undefined;

  const getEndsAt = (recording: VoiceRecording) =>
    recording.durationMs
      ? new Date(new Date(recording.recordedAt).getTime() + recording.durationMs).toISOString()
      : undefined;

  const createMeetingInput = (
    recording: VoiceRecording,
    overrideContactIds?: number[],
    meetingRecording?: MeetingRecordingInput,
  ): CreateMeetingInput => {
    const baseInput: CreateMeetingInput = {
      contactIds: getContactIds(recording, overrideContactIds),
      startsAt: recording.recordedAt,
      endsAt: getEndsAt(recording),
      location: getLocation(recording),
    };

    if (!meetingRecording) {
      return baseInput;
    }

    return { ...baseInput, recording: meetingRecording };
  };

  const finalizeUploadedRecording = async (recording: VoiceRecording) => {
    const { repository, fileStorage, logger } = adapters;

    await repository.updateUploadStatus(recording.id, 'uploaded');

    await fileStorage.deleteRecordingFile(recording.filePath).catch((e) => {
      logger.captureException(e as Error);
    });
    await repository.deleteById(recording.id).catch((e) => {
      logger.captureException(e as Error);
    });
  };

  const createMeetingOnly = async (
    recording: VoiceRecording,
    overrideContactIds?: number[],
    triggeredBy: 'auto' | 'retry' | 'assign_contacts' = 'auto',
  ): Promise<string | null> => {
    const { repository, fileTransferAdapter, logger, analytics, networkAdapter } = adapters;

    if (!(await adapters.hasAiTranscriptionConsent())) {
      return null;
    }

    const networkState = await networkAdapter.getNetworkState();
    analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_UPLOAD_ATTEMPTED, {
      [ANALYTICS_PARAMS.RECORDING_ID]: recording.id,
      [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.id,
      [ANALYTICS_PARAMS.ACCOUNT_ID]: recording.accountId ?? '',
      [ANALYTICS_PARAMS.MEETING_ID]: recording.meetingId ?? '',
      [ANALYTICS_PARAMS.PATH]: 'meeting_only',
      [ANALYTICS_PARAMS.TRIGGERED_BY]: triggeredBy,
      [ANALYTICS_PARAMS.NETWORK_TYPE]: networkState.type ?? 'unknown',
    });

    const stage: { value: 'create_meeting' } = { value: 'create_meeting' };

    try {
      const meetingResult = await fileTransferAdapter.createMeeting(
        createMeetingInput(recording, overrideContactIds),
      );

      await repository.updateMeetingId(recording.id, meetingResult.id);

      analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_UPLOAD_RESULT, {
        [ANALYTICS_PARAMS.RECORDING_ID]: recording.id,
        [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.id,
        [ANALYTICS_PARAMS.ACCOUNT_ID]: recording.accountId ?? '',
        [ANALYTICS_PARAMS.OUTCOME]: 'succeeded',
      });

      return meetingResult.id;
    } catch (meetingError) {
      logger.captureException(meetingError as Error);
      analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_UPLOAD_RESULT, {
        [ANALYTICS_PARAMS.RECORDING_ID]: recording.id,
        [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.id,
        [ANALYTICS_PARAMS.ACCOUNT_ID]: recording.accountId ?? '',
        [ANALYTICS_PARAMS.OUTCOME]: 'failed',
        [ANALYTICS_PARAMS.ERROR_STAGE]: stage.value,
        [ANALYTICS_PARAMS.ERROR_CODE]: (meetingError as Error).name,
      });

      return null;
    }
  };

  const runUploadFileAndAttach = async (
    recording: VoiceRecording,
    failureStatus: UploadStatus = 'failed',
    triggeredBy: 'auto' | 'retry' | 'assign_contacts' = 'auto',
  ): Promise<string | null> => {
    const { repository, fileStorage, fileTransferAdapter, logger, analytics, networkAdapter } =
      adapters;
    const mimeType = 'audio/m4a';

    if (!(await adapters.hasAiTranscriptionConsent())) {
      return null;
    }

    if (!recording.meetingId) {
      return null;
    }

    const networkState = await networkAdapter.getNetworkState();
    analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_UPLOAD_ATTEMPTED, {
      [ANALYTICS_PARAMS.RECORDING_ID]: recording.id,
      [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.id,
      [ANALYTICS_PARAMS.ACCOUNT_ID]: recording.accountId ?? '',
      [ANALYTICS_PARAMS.MEETING_ID]: recording.meetingId ?? '',
      [ANALYTICS_PARAMS.PATH]: 'upload_and_attach',
      [ANALYTICS_PARAMS.TRIGGERED_BY]: triggeredBy,
      [ANALYTICS_PARAMS.NETWORK_TYPE]: networkState.type ?? 'unknown',
    });

    const stage: { value: 'get_file_size' | 'generate_url' | 'put_file' | 'attach' } = {
      value: 'get_file_size',
    };

    try {
      await repository.updateUploadStatus(recording.id, 'uploading');

      const resolvedPath = fileStorage.resolveRecordingUri(recording.filePath);
      stage.value = 'get_file_size';
      const fileSize = await fileStorage.getFileSize(resolvedPath);

      stage.value = 'generate_url';
      const { uploadUrl, storagePath, headers } =
        await fileTransferAdapter.generateRecordingUploadUrl({
          uuid: recording.id,
          fileExtension: 'm4a',
        });

      stage.value = 'put_file';
      await fileTransferAdapter.uploadFile(uploadUrl, headers, resolvedPath, mimeType);

      stage.value = 'attach';
      await fileTransferAdapter.attachMeetingRecording({
        meetingId: recording.meetingId,
        recording: {
          uuid: recording.id,
          storagePath,
          fileExtension: 'm4a',
          durationSeconds: getDurationSeconds(recording),
          fileSizeBytes: fileSize,
        },
      });

      await finalizeUploadedRecording(recording);

      analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_UPLOAD_RESULT, {
        [ANALYTICS_PARAMS.RECORDING_ID]: recording.id,
        [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.id,
        [ANALYTICS_PARAMS.ACCOUNT_ID]: recording.accountId ?? '',
        [ANALYTICS_PARAMS.OUTCOME]: 'succeeded',
      });

      return recording.meetingId;
    } catch (uploadError) {
      // The meeting was deleted, or belongs to an account the user has left.
      // Nothing here can recover it, so tell the user instead of Sentry.
      if (hasGraphQLErrorCode(uploadError, ERROR_CODES.NotFound)) {
        logger.addBreadcrumb('Meeting missing for recording upload', {
          meetingId: recording.meetingId,
        });
        storeApi.setState?.({
          alert: { text: 'recording.errors.recordingNotFound', variant: 'error' },
        });
        await repository.updateUploadStatus(recording.id, failureStatus).catch((statusError) => {
          logger.captureException(statusError as Error);
        });

        return null;
      }

      logger.captureException(uploadError as Error);
      analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_UPLOAD_RESULT, {
        [ANALYTICS_PARAMS.RECORDING_ID]: recording.id,
        [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.id,
        [ANALYTICS_PARAMS.ACCOUNT_ID]: recording.accountId ?? '',
        [ANALYTICS_PARAMS.OUTCOME]: 'failed',
        [ANALYTICS_PARAMS.ERROR_STAGE]: stage.value,
        [ANALYTICS_PARAMS.ERROR_CODE]: (uploadError as Error).name,
      });
      await repository.updateUploadStatus(recording.id, failureStatus).catch((statusError) => {
        logger.captureException(statusError as Error);
      });

      return null;
    }
  };

  const runUploadRecording = async (
    recording: VoiceRecording,
    overrideContactIds?: number[],
    failureStatus: UploadStatus = 'failed',
    triggeredBy: 'auto' | 'retry' | 'assign_contacts' = 'auto',
  ): Promise<string | null> => {
    const { repository, fileStorage, fileTransferAdapter, logger, analytics, networkAdapter } =
      adapters;
    const mimeType = 'audio/m4a';

    if (!(await adapters.hasAiTranscriptionConsent())) {
      return null;
    }

    const contactIds =
      overrideContactIds ?? (recording.contactId === null ? [] : [recording.contactId]);

    const networkState = await networkAdapter.getNetworkState();
    analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_UPLOAD_ATTEMPTED, {
      [ANALYTICS_PARAMS.RECORDING_ID]: recording.id,
      [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.id,
      [ANALYTICS_PARAMS.ACCOUNT_ID]: recording.accountId ?? '',
      [ANALYTICS_PARAMS.MEETING_ID]: recording.meetingId ?? '',
      [ANALYTICS_PARAMS.PATH]: 'upload_and_create',
      [ANALYTICS_PARAMS.TRIGGERED_BY]: triggeredBy,
      [ANALYTICS_PARAMS.NETWORK_TYPE]: networkState.type ?? 'unknown',
    });

    const stage: { value: 'get_file_size' | 'generate_url' | 'put_file' | 'create_meeting' } = {
      value: 'get_file_size',
    };

    try {
      await repository.updateUploadStatus(recording.id, 'uploading');

      const resolvedPath = fileStorage.resolveRecordingUri(recording.filePath);
      stage.value = 'get_file_size';
      const fileSize = await fileStorage.getFileSize(resolvedPath);

      stage.value = 'generate_url';
      const { uploadUrl, storagePath, headers } =
        await fileTransferAdapter.generateRecordingUploadUrl({
          uuid: recording.id,
          fileExtension: 'm4a',
        });

      stage.value = 'put_file';
      await fileTransferAdapter.uploadFile(uploadUrl, headers, resolvedPath, mimeType);

      stage.value = 'create_meeting';
      const meetingResult = await fileTransferAdapter.createMeeting(
        createMeetingInput(recording, contactIds, {
          uuid: recording.id,
          storagePath,
          fileExtension: 'm4a',
          durationSeconds: getDurationSeconds(recording),
          fileSizeBytes: fileSize,
        }),
      );

      await finalizeUploadedRecording(recording);

      analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_UPLOAD_RESULT, {
        [ANALYTICS_PARAMS.RECORDING_ID]: recording.id,
        [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.id,
        [ANALYTICS_PARAMS.ACCOUNT_ID]: recording.accountId ?? '',
        [ANALYTICS_PARAMS.OUTCOME]: 'succeeded',
      });

      return meetingResult.id;
    } catch (uploadError) {
      logger.captureException(uploadError as Error);
      analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_UPLOAD_RESULT, {
        [ANALYTICS_PARAMS.RECORDING_ID]: recording.id,
        [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.id,
        [ANALYTICS_PARAMS.ACCOUNT_ID]: recording.accountId ?? '',
        [ANALYTICS_PARAMS.OUTCOME]: 'failed',
        [ANALYTICS_PARAMS.ERROR_STAGE]: stage.value,
        [ANALYTICS_PARAMS.ERROR_CODE]: (uploadError as Error).name,
      });
      await repository.updateUploadStatus(recording.id, failureStatus).catch((statusError) => {
        logger.captureException(statusError as Error);
      });

      return null;
    }
  };

  const uploadFileAndAttach = (
    recording: VoiceRecording,
    failureStatus: UploadStatus = 'failed',
    triggeredBy: 'auto' | 'retry' | 'assign_contacts' = 'auto',
  ): Promise<string | null> => {
    if (inFlightUploads.has(recording.id)) {
      return Promise.resolve(null);
    }

    return withInFlightUpload(recording.id, () =>
      runUploadFileAndAttach(recording, failureStatus, triggeredBy),
    );
  };

  const uploadRecording = (
    recording: VoiceRecording,
    overrideContactIds?: number[],
    failureStatus: UploadStatus = 'failed',
    triggeredBy: 'auto' | 'retry' | 'assign_contacts' = 'auto',
  ): Promise<string | null> => {
    if (inFlightUploads.has(recording.id)) {
      return Promise.resolve(null);
    }

    return withInFlightUpload(recording.id, () =>
      runUploadRecording(recording, overrideContactIds, failureStatus, triggeredBy),
    );
  };

  return create<VoiceRecordingState & VoiceRecordingActions>((set, get) => {
    storeApi.setState = set;

    adapters.audioRecorder.setOnRecordingError((error) => {
      const { recording } = get();
      const accountId = recording.accountId;
      const durationMs = recording.startedAt ? Date.now() - recording.startedAt : 0;

      adapters.analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
        [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId ?? '',
        [ANALYTICS_PARAMS.OUTCOME]: 'recorder_error',
        [ANALYTICS_PARAMS.DURATION_MS]: durationMs,
        [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.recordingSessionId,
      });
      void adapters.storage
        .deleteItem(APP_KILLED_MARKER_KEY)
        .catch(adapters.logger.captureException);

      adapters.logger.captureMessage(error, 'error');
      clearRecordingLifecycle();
      adapters.audioRecorder.setOnMeteringUpdate(null);
      endLiveSurface();
      const alertText =
        typeof error === 'string' && error.includes('encoder stalled')
          ? 'recording.errors.encoderStalled'
          : 'recording.errors.recordingFailed';
      set({
        recording: initialRecording,
        meteringLevel: 0,
        alert: { text: alertText, variant: 'error' },
      });
    });

    adapters.recordingLiveSurface.setOnAction((action, sessionId) => {
      const { recording } = get();
      if (recording.recordingSessionId !== sessionId) return;

      if (action === 'pause') {
        void get().pauseRecording();
        return;
      }
      if (action === 'resume') {
        void get().resumeRecording();
      }
    });

    const applyConfirmedPause = () => {
      const { recording } = get();
      if (recording.status !== 'recording') return;

      const segmentElapsed = recording.segmentStartedAt
        ? Date.now() - recording.segmentStartedAt
        : 0;

      set({
        recording: {
          ...recording,
          status: 'recording_paused',
          elapsedMs: recording.elapsedMs + segmentElapsed,
          segmentStartedAt: null,
        },
        meteringLevel: 0,
      });
    };

    const applyConfirmedResume = () => {
      const { recording } = get();
      if (recording.status !== 'recording_paused') return;

      set({
        recording: {
          ...recording,
          status: 'recording',
          segmentStartedAt: Date.now(),
        },
      });
    };

    adapters.audioRecorder.setOnStateUpdate((state) => {
      const { recording } = get();
      if (recording.recordingSessionId === null) return;

      if (state === 'paused') {
        applyConfirmedPause();
        return;
      }
      if (state === 'recording') {
        applyConfirmedResume();
      }
    });

    const onAppStateChange = (status: AppStateStatus) => {
      // Background recording is supported; keep the session running. Track the
      // app state so a low-storage stop can notify when the app is not visible
      // and the warning banner stays foreground-only.
      currentAppState.value = status;
    };

    const beginRecording = async (
      contactId: number | null,
      userId: string,
      accountId: string,
      onAutoStopped?: VoiceRecordingStartCallbacks['onAutoStopped'],
      meetingId: string | null = null,
    ) => {
      const { audioRecorder, locationProvider, logger, analytics, storage } = adapters;
      const { recording } = get();

      if (recording.status !== 'idle') {
        return;
      }

      if (!(await adapters.hasAiTranscriptionConsent())) {
        return;
      }

      if (!userId || !accountId) {
        set({ alert: { text: 'recording.errors.invalidUserOrAccount', variant: 'error' } });
        return;
      }

      set({ recording: { ...get().recording, status: 'starting' } });

      try {
        const hasMicPermission = await audioRecorder.requestPermission();
        if (!hasMicPermission) {
          analytics.track(ANALYTICS_EVENTS.APP_PERMISSION_DENIED, {
            [ANALYTICS_PARAMS.PERMISSION]: 'microphone',
          });
          analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
            [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId,
            [ANALYTICS_PARAMS.OUTCOME]: 'mic_permission_denied',
            [ANALYTICS_PARAMS.DURATION_MS]: 0,
            [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.recordingSessionId,
          });
          void storage.deleteItem(APP_KILLED_MARKER_KEY).catch(logger.captureException);
          set({
            recording: { ...get().recording, status: 'idle' },
            alert: { text: 'recording.errors.microphonePermissionDenied', variant: 'error' },
          });
          return;
        }

        const hasLocationPermission = await locationProvider.requestPermission().catch(() => false);
        if (hasLocationPermission === false) {
          analytics.track(ANALYTICS_EVENTS.APP_PERMISSION_DENIED, {
            [ANALYTICS_PARAMS.PERMISSION]: 'location',
          });
        }
        audioRecorder.setOnMeteringUpdate((level) => {
          set({ meteringLevel: level });
        });
        await audioRecorder.startRecording({ timeoutMs: config.startTimeoutMs });
        const recordingSessionId = adapters.generateId();
        const now = Date.now();

        const sampleIntervalMs =
          config.storageSampleIntervalMs ?? DEFAULT_STORAGE_SAMPLE_INTERVAL_MS;
        const sampleEveryTicks = Math.max(1, Math.round(sampleIntervalMs / LIFECYCLE_TICK_MS));
        storageMonitor.ticksUntilNextSample = 0;
        storageMonitor.availableBytes = null;

        timer.ref = setInterval(() => {
          const { recording: current } = get();
          if (current.status !== 'recording' && current.status !== 'recording_paused') {
            return;
          }

          const now = Date.now();

          storageMonitor.ticksUntilNextSample -= 1;
          if (storageMonitor.ticksUntilNextSample <= 0) {
            storageMonitor.ticksUntilNextSample = sampleEveryTicks;

            const sampleAvailableDiskSpace = (): number | null => {
              try {
                const bytes = adapters.fileStorage.getAvailableDiskSpaceBytes();
                storageMonitor.availableBytes = bytes;
                return bytes;
              } catch (error) {
                adapters.logger.captureException(error as Error);
                return null;
              }
            };
            const availableBytes = sampleAvailableDiskSpace();

            if (availableBytes !== null) {
              const lowStorageThresholdBytes =
                config.lowStorageThresholdBytes ?? DEFAULT_LOW_STORAGE_THRESHOLD_BYTES;
              const warningThresholdBytes =
                config.lowStorageWarningThresholdBytes ??
                DEFAULT_LOW_STORAGE_WARNING_THRESHOLD_BYTES;

              if (availableBytes <= lowStorageThresholdBytes) {
                void get()
                  .stopRecording('low_storage')
                  .then((meetingId) => {
                    onAutoStopped?.(meetingId);
                  });
                return;
              }

              set({
                lowStorageWarning:
                  availableBytes < warningThresholdBytes && currentAppState.value === 'active',
              });
            }
          }

          if (current.status !== 'recording') {
            return;
          }
          const currentDuration = current.segmentStartedAt
            ? current.elapsedMs + (now - current.segmentStartedAt)
            : current.elapsedMs;
          if (currentDuration >= config.maxRecordingDurationMs) {
            void get()
              .stopRecording('duration_limit')
              .then((meetingId) => {
                onAutoStopped?.(meetingId);
              });
          }
        }, 1000);

        set({
          recording: {
            ...get().recording,
            status: 'recording',
            recordingSessionId,
            contactId,
            meetingId,
            userId,
            accountId,
            startedAt: now,
            elapsedMs: 0,
            segmentStartedAt: now,
          },
        });

        try {
          await adapters.recordingLiveSurface.start({
            recordingSessionId,
            status: 'recording',
            level: 0,
            timerStartedAt: now,
            pausedAt: null,
          });
          clearLiveSurfaceTimer();
          // ActivityKit enforces a per-app update budget: while paused nothing
          // changes on the surface, so push the paused snapshot once and skip
          // further ticks until recording resumes.
          const hasSentPausedSnapshot = { ref: false };
          liveSurfaceTimer.ref = setInterval(() => {
            const { recording: current, meteringLevel } = get();
            if (current.recordingSessionId !== recordingSessionId) return;

            const isRecording = current.status === 'recording';
            if (!isRecording && hasSentPausedSnapshot.ref) return;
            hasSentPausedSnapshot.ref = !isRecording;

            const currentTime = Date.now();
            void adapters.recordingLiveSurface
              .update({
                recordingSessionId,
                status: isRecording ? 'recording' : 'paused',
                level: isRecording ? meteringLevel : 0,
                timerStartedAt: isRecording
                  ? (current.segmentStartedAt ?? currentTime) - current.elapsedMs
                  : currentTime - current.elapsedMs,
                pausedAt: isRecording ? null : currentTime,
              })
              .catch(adapters.logger.captureException);
          }, LIVE_SURFACE_UPDATE_INTERVAL_MS);
        } catch (e) {
          adapters.logger.captureException(e as Error);
        }

        clearAppStateListener();
        appStateUnsubscribe.ref = adapters.appState.addEventListener(onAppStateChange);

        const source =
          contactId !== null ? 'contact' : meetingId !== null ? 'meeting' : 'standalone';

        analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_STARTED, {
          [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId,
          [ANALYTICS_PARAMS.USER_ID]: userId,
          [ANALYTICS_PARAMS.CONTACT_ID]: contactId != null ? String(contactId) : null,
          [ANALYTICS_PARAMS.MEETING_ID]: meetingId ?? null,
          [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recordingSessionId,
          [ANALYTICS_PARAMS.SOURCE]: source,
        });
        void storage
          .setItem(
            APP_KILLED_MARKER_KEY,
            JSON.stringify({ startedAt: now, accountId, recordingSessionId }),
          )
          .catch(logger.captureException);
      } catch (e) {
        clearRecordingLifecycle();
        endLiveSurface();

        if (isNotificationPermissionDeniedError(e)) {
          analytics.track(ANALYTICS_EVENTS.APP_PERMISSION_DENIED, {
            [ANALYTICS_PARAMS.PERMISSION]: 'notification',
          });
          analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
            [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId,
            [ANALYTICS_PARAMS.OUTCOME]: 'notification_permission_denied',
            [ANALYTICS_PARAMS.DURATION_MS]: 0,
            [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.recordingSessionId,
          });
          void storage.deleteItem(APP_KILLED_MARKER_KEY).catch(logger.captureException);
          set({
            recording: initialRecording,
            alert: { text: 'recording.errors.notificationPermissionDenied', variant: 'error' },
          });
          return;
        }

        if (isStartCancelledError(e)) {
          analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
            [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId,
            [ANALYTICS_PARAMS.OUTCOME]: 'cancelled',
            [ANALYTICS_PARAMS.DURATION_MS]: 0,
            [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.recordingSessionId,
          });
          void storage.deleteItem(APP_KILLED_MARKER_KEY).catch(logger.captureException);
          set({ recording: initialRecording });

          return;
        }

        analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
          [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId,
          [ANALYTICS_PARAMS.OUTCOME]: 'recorder_error',
          [ANALYTICS_PARAMS.DURATION_MS]: 0,
          [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.recordingSessionId,
        });
        void storage.deleteItem(APP_KILLED_MARKER_KEY).catch(logger.captureException);
        logger.captureException(e as Error);
        set({
          recording: initialRecording,
          alert: { text: 'recording.errors.recordingFailed', variant: 'error' },
        });
      }
    };

    const setItemStatus = (id: string, status: UploadStatus) => {
      const { recording } = get();
      set({
        recording: {
          ...recording,
          items: recording.items.map((r) => (r.id === id ? { ...r, uploadStatus: status } : r)),
        },
      });
    };

    const setUploadFailedAlertIfIdle = () => {
      if (get().recording.status !== 'idle') {
        return;
      }

      set({ alert: { text: 'recording.errors.uploadFailed', variant: 'error' } });
    };

    const handleSuccessfulUploadedRecording = (recordingId: string) => {
      set({
        recording: {
          ...get().recording,
          items: get().recording.items.filter((r) => r.id !== recordingId),
        },
        upload: { ...get().upload, completedAt: Date.now() },
      });
    };

    const setMeetingCreatedAlertIfIdle = () => {
      if (get().recording.status !== 'idle') {
        return;
      }

      set({
        alert: { text: 'recording.actions.meetingCreated', variant: 'success' },
      });
    };

    const setUploadSuccessAlert = () => {
      set({ alert: { text: 'recording.actions.uploadSuccess', variant: 'success' } });
    };

    const retryPendingUpload = async (
      pendingRecording: VoiceRecording,
    ): Promise<'uploaded' | 'meeting_created' | 'failure' | 'skipped'> => {
      if (!(await adapters.hasAiTranscriptionConsent())) {
        return 'skipped';
      }

      if (pendingRecording.meetingId) {
        const allowed = await adapters.canUpload();
        if (!allowed) {
          return 'skipped';
        }

        const meetingId = await uploadFileAndAttach(pendingRecording, 'failed', 'retry');

        return meetingId ? 'uploaded' : 'failure';
      }

      const allowed = await adapters.canUpload();
      if (allowed) {
        const meetingId = await uploadRecording(pendingRecording, undefined, 'failed', 'retry');

        return meetingId ? 'uploaded' : 'failure';
      }

      const hasAnyNetwork = await adapters.hasAnyNetwork();
      if (!hasAnyNetwork) {
        return 'skipped';
      }

      const createdMeetingId = await createMeetingOnly(pendingRecording, undefined, 'retry');

      return createdMeetingId ? 'meeting_created' : 'failure';
    };

    return {
      recording: initialRecording,
      upload: initialUpload,
      alert: null,
      meteringLevel: 0,
      lowStorageWarning: false,

      requestMicrophonePermission: async (): Promise<boolean> => {
        const granted = await adapters.audioRecorder.requestPermission();
        if (!granted) {
          set({ alert: { text: 'recording.errors.microphonePermissionDenied', variant: 'error' } });
        }
        return granted;
      },

      startRecording: async (
        contactId: number,
        userId: string,
        accountId: string,
        callbacks?: VoiceRecordingStartCallbacks,
      ) => {
        await beginRecording(contactId, userId, accountId, callbacks?.onAutoStopped);
      },

      startStandaloneRecording: async (
        userId: string,
        accountId: string,
        callbacks?: VoiceRecordingStartCallbacks,
      ) => {
        await beginRecording(null, userId, accountId, callbacks?.onAutoStopped);
      },

      startRecordingForMeeting: async (
        meetingId: string,
        userId: string,
        accountId: string,
        callbacks?: VoiceRecordingStartCallbacks,
      ) => {
        await beginRecording(null, userId, accountId, callbacks?.onAutoStopped, meetingId);
      },

      stopRecording: async (trigger: RecordingStopTrigger = 'user'): Promise<string | null> => {
        const {
          audioRecorder,
          locationProvider,
          repository,
          fileStorage,
          generateId,
          logger,
          analytics,
          storage,
        } = adapters;
        const { recording } = get();

        clearRecordingLifecycle();
        audioRecorder.setOnMeteringUpdate(null);
        endLiveSurface();

        if (recording.status !== 'recording' && recording.status !== 'recording_paused') {
          return null;
        }

        const startedAt = recording.startedAt;

        set({
          recording: { ...get().recording, status: 'stopping' },
          meteringLevel: 0,
          lowStorageWarning: false,
        });

        if (recording.userId === null || recording.accountId === null) {
          set({
            recording: initialRecording,
            alert: { text: 'recording.errors.invalidState', variant: 'error' },
          });

          return null;
        }

        const userId = recording.userId;
        const accountId = recording.accountId;

        const savedRecording = await (async (): Promise<VoiceRecording | null> => {
          try {
            const { uri, durationMs } = await audioRecorder.stopRecording();
            const id = recording.recordingSessionId ?? generateId();

            const location = await locationProvider.getCurrentPosition().catch(() => null);
            const address = location
              ? await locationProvider.reverseGeocode(location).catch(() => null)
              : null;

            const filePath = await fileStorage.moveRecordingToDocuments(uri, id);

            const now = new Date().toISOString();
            const saved: VoiceRecording = {
              id,
              contactId: recording.contactId,
              userId,
              accountId,
              meetingId: recording.meetingId,
              filePath,
              durationMs,
              latitude: location?.latitude ?? null,
              longitude: location?.longitude ?? null,
              address,
              recordedAt: recording.startedAt ? new Date(recording.startedAt).toISOString() : now,
              uploadStatus: 'uploading',
              createdAt: now,
              updatedAt: now,
            };

            try {
              await repository.insert(saved);

              if (get().recording.status === 'stopping') {
                set({ recording: initialRecording });
              }

              await (recording.contactId
                ? get().loadRecordings(recording.contactId, accountId)
                : get().loadAllRecordings(accountId));

              return saved;
            } catch (e) {
              await fileStorage.deleteRecordingFile(saved.filePath).catch((deleteError: Error) => {
                logger.captureException(deleteError);
              });

              throw e;
            }
          } catch (e) {
            logger.captureException(e as Error);

            if (get().recording.status === 'stopping') {
              set({
                recording: initialRecording,
                alert: { text: 'recording.errors.recordingFailed', variant: 'error' },
              });
            }

            return null;
          }
        })();

        if (!savedRecording) {
          analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
            [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId ?? '',
            [ANALYTICS_PARAMS.OUTCOME]: 'recorder_error',
            [ANALYTICS_PARAMS.DURATION_MS]: startedAt ? Date.now() - startedAt : 0,
            [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.recordingSessionId,
            ...(trigger !== 'user' ? { [ANALYTICS_PARAMS.TRIGGERED_BY]: trigger } : {}),
          });
          void storage.deleteItem(APP_KILLED_MARKER_KEY).catch(logger.captureException);

          return null;
        }

        analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
          [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId ?? '',
          [ANALYTICS_PARAMS.OUTCOME]: 'saved',
          [ANALYTICS_PARAMS.DURATION_MS]: savedRecording.durationMs ?? 0,
          [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: savedRecording.id,
          [ANALYTICS_PARAMS.TRIGGERED_BY]: trigger,
        });
        void storage.deleteItem(APP_KILLED_MARKER_KEY).catch(logger.captureException);

        if (trigger === 'low_storage') {
          const lowStorageThresholdBytes =
            config.lowStorageThresholdBytes ?? DEFAULT_LOW_STORAGE_THRESHOLD_BYTES;
          logger.captureMessage(
            'Voice input stopped and saved because device storage is low',
            'warning',
            {
              availableBytes: storageMonitor.availableBytes,
              thresholdBytes: lowStorageThresholdBytes,
            },
          );
          set({
            alert: { text: 'recording.errors.lowStorageStopped', variant: 'error' },
          });
          if (currentAppState.value !== 'active') {
            await adapters
              .notifyRecordingStopped('low_storage')
              .catch((e) => logger.captureException(e as Error));
          }
        }

        // A low-storage stop keeps its own snackbar so the user learns why the
        // recording stopped; upload outcome alerts must not overwrite it.
        const reportUploadFailed = () => {
          if (trigger !== 'low_storage') setUploadFailedAlertIfIdle();
        };
        const reportMeetingCreated = () => {
          if (trigger !== 'low_storage') setMeetingCreatedAlertIfIdle();
        };
        const reportUploadSuccess = () => {
          if (trigger !== 'low_storage') setUploadSuccessAlert();
        };

        try {
          const allowed = await adapters.canUpload();

          if (savedRecording.meetingId) {
            if (!allowed) {
              await repository.updateUploadStatus(savedRecording.id, 'pending').catch((e) => {
                logger.captureException(e as Error);
              });
              await get().loadPendingUploadMeetingIds();
              await adapters.notifyUploadPending(savedRecording.meetingId).catch((e) => {
                adapters.logger.captureException(e as Error);
              });

              return savedRecording.meetingId;
            }

            const attachedMeetingId = await uploadFileAndAttach(savedRecording, 'failed', 'auto');
            if (!attachedMeetingId) {
              reportUploadFailed();

              return null;
            }

            handleSuccessfulUploadedRecording(savedRecording.id);
            reportUploadSuccess();

            return attachedMeetingId;
          }

          if (!allowed) {
            const hasAnyNetwork = await adapters.hasAnyNetwork();
            if (!hasAnyNetwork) {
              await repository.updateUploadStatus(savedRecording.id, 'pending').catch((e) => {
                logger.captureException(e as Error);
              });
              return null;
            }

            const meetingId = await createMeetingOnly(savedRecording, undefined, 'auto');
            if (!meetingId) {
              reportUploadFailed();

              return null;
            }

            await get().loadPendingUploadMeetingIds();
            await adapters.notifyUploadPending(meetingId).catch((e) => {
              adapters.logger.captureException(e as Error);
            });
            reportMeetingCreated();

            return null;
          }

          const meetingId = await uploadRecording(savedRecording, undefined, 'failed', 'auto');
          if (!meetingId) {
            reportUploadFailed();

            return null;
          }

          handleSuccessfulUploadedRecording(savedRecording.id);
          reportUploadSuccess();

          return meetingId;
        } catch (e) {
          logger.captureException(e as Error);
          reportUploadFailed();
        }

        return null;
      },

      dismissLowStorageWarning: () => {
        set({ lowStorageWarning: false });
      },

      shouldConfirmLowStorageStart: async (): Promise<boolean> => {
        const preFlightThresholdBytes =
          config.lowStoragePreFlightThresholdBytes ??
          DEFAULT_LOW_STORAGE_PRE_FLIGHT_THRESHOLD_BYTES;

        try {
          return adapters.fileStorage.getAvailableDiskSpaceBytes() < preFlightThresholdBytes;
        } catch (error) {
          adapters.logger.captureException(error as Error);

          return false;
        }
      },

      cancelRecording: async () => {
        const { audioRecorder, logger, analytics, storage } = adapters;
        clearRecordingLifecycle();
        audioRecorder.setOnMeteringUpdate(null);
        endLiveSurface();

        const { recording } = get();
        const accountId = recording.accountId;
        const durationMs = recording.startedAt ? Date.now() - recording.startedAt : 0;

        try {
          await audioRecorder.cancelRecording();
          set({ recording: initialRecording, meteringLevel: 0, lowStorageWarning: false });
        } catch (e) {
          logger.captureException(e as Error);
          set({ alert: { text: 'recording.errors.recordingFailed', variant: 'error' } });
        }

        analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
          [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId ?? '',
          [ANALYTICS_PARAMS.OUTCOME]: 'cancelled',
          [ANALYTICS_PARAMS.DURATION_MS]: durationMs,
          [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.recordingSessionId,
        });
        void storage.deleteItem(APP_KILLED_MARKER_KEY).catch(logger.captureException);
      },

      reset: async () => {
        const { audioRecorder, analytics, storage, logger } = adapters;
        clearRecordingLifecycle();
        audioRecorder.setOnMeteringUpdate(null);
        endLiveSurface();

        const { recording } = get();
        const accountId = recording.accountId;
        const durationMs = recording.startedAt ? Date.now() - recording.startedAt : 0;

        if (recording.status !== 'idle') {
          await audioRecorder.cancelRecording().catch(() => {});
        }

        set({
          recording: initialRecording,
          upload: initialUpload,
          alert: null,
          meteringLevel: 0,
          lowStorageWarning: false,
        });

        if (accountId) {
          analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
            [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId,
            [ANALYTICS_PARAMS.OUTCOME]: 'reset',
            [ANALYTICS_PARAMS.DURATION_MS]: durationMs,
            [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recording.recordingSessionId,
          });
          void storage.deleteItem(APP_KILLED_MARKER_KEY).catch(logger.captureException);
        }
      },

      replayAppKilledMarker: async () => {
        const { analytics, storage, logger } = adapters;

        try {
          const marker = await storage.getItem(APP_KILLED_MARKER_KEY);
          if (!marker) {
            return;
          }

          // iOS Live Activities are OS-owned and survive process termination, so
          // a surface left over from the killed session must be ended explicitly.
          await adapters.recordingLiveSurface.endStaleInstances().catch(logger.captureException);

          const { startedAt, accountId, recordingSessionId } = JSON.parse(marker) as {
            startedAt?: number;
            accountId?: string;
            recordingSessionId?: string;
          };

          if (!startedAt || !accountId) {
            return;
          }

          const durationMs = Date.now() - startedAt;

          analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RECORDING_ENDED, {
            [ANALYTICS_PARAMS.ACCOUNT_ID]: accountId,
            [ANALYTICS_PARAMS.OUTCOME]: 'app_killed',
            [ANALYTICS_PARAMS.DURATION_MS]: durationMs,
            [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: recordingSessionId ?? null,
          });
          logger.captureMessage('Recording session ended by app kill', 'warning', {
            accountId,
            recordingSessionId,
            durationMs,
          });
          await storage.deleteItem(APP_KILLED_MARKER_KEY);
          set({
            alert: { text: 'recording.errors.recordingInterrupted', variant: 'error' },
          });
          await adapters.notifyRecordingStopped('app_killed').catch(logger.captureException);
        } catch (e) {
          logger.captureException(e as Error);
        }
      },

      loadRecordings: async (contactId: number, accountId: string) => {
        const { repository, logger } = adapters;
        try {
          const items = await repository.findByContactId(contactId, accountId);
          set({ recording: { ...get().recording, items } });
        } catch (e) {
          logger.captureException(e as Error);
          set({ alert: { text: 'contacts.profile.loadError', variant: 'error' } });
        }
      },

      loadAllRecordings: async (accountId: string) => {
        const { repository, logger } = adapters;
        try {
          const items = await repository.findAllByAccountId(accountId);
          set({ recording: { ...get().recording, items } });
        } catch (e) {
          logger.captureException(e as Error);
          set({ alert: { text: 'recording.errors.loadAllFailed', variant: 'error' } });
        }
      },

      assignContactsAndUpload: async (
        recordingId: string,
        contactIds: number[],
      ): Promise<boolean> => {
        const { repository, canUpload, logger } = adapters;

        if (contactIds.length === 0) {
          return false;
        }

        const rec = await repository.findById(recordingId);
        if (!rec || rec.uploadStatus !== 'pending_contact') {
          set({ alert: { text: 'recording.errors.recordingNotFound', variant: 'error' } });
          return false;
        }

        const allowed = await canUpload();
        if (!allowed) {
          set({ alert: { text: 'recording.errors.uploadFailed', variant: 'error' } });
          return false;
        }

        try {
          setItemStatus(recordingId, 'uploading');
          const meetingId = await uploadRecording(
            rec,
            contactIds,
            'pending_contact',
            'assign_contacts',
          );

          if (meetingId) {
            set({
              recording: {
                ...get().recording,
                items: get().recording.items.filter((r) => r.id !== recordingId),
              },
              upload: { ...get().upload, completedAt: Date.now() },
              alert: { text: 'recording.actions.assignContactsSuccess', variant: 'success' },
            });
            return true;
          }

          setItemStatus(recordingId, 'pending_contact');
          set({ alert: { text: 'recording.errors.assignContactsFailed', variant: 'error' } });
          return false;
        } catch (assignError) {
          logger.captureException(assignError as Error);
          setItemStatus(recordingId, 'pending_contact');
          set({ alert: { text: 'recording.errors.assignContactsFailed', variant: 'error' } });
          return false;
        }
      },

      deleteRecording: async (id: string): Promise<boolean> => {
        const { repository, fileStorage, logger } = adapters;
        const { recording } = get();

        try {
          const found = await repository.findById(id);
          if (!found) {
            return true;
          }

          await fileStorage.deleteRecordingFile(found.filePath);
          await repository.deleteById(id);

          set({
            recording: {
              ...get().recording,
              items: recording.items.filter((r) => r.id !== id),
            },
            alert: { text: 'recording.actions.deleteSuccess', variant: 'success' },
          });

          await get().loadTotalSize();
          return true;
        } catch (e) {
          logger.captureException(e as Error);
          set({ alert: { text: 'recording.errors.deleteFailed', variant: 'error' } });
          return false;
        }
      },

      pauseRecording: async () => {
        const { audioRecorder, logger } = adapters;
        const { recording } = get();

        if (recording.status !== 'recording') return;

        try {
          await audioRecorder.pauseRecording();
          applyConfirmedPause();
        } catch (e) {
          logger.captureException(e as Error);
        }
      },

      resumeRecording: async () => {
        const { audioRecorder, logger } = adapters;
        const { recording } = get();

        if (recording.status !== 'recording_paused') return;

        try {
          await audioRecorder.resumeRecording();
          applyConfirmedResume();
        } catch (e) {
          logger.captureException(e as Error);
        }
      },

      retryPendingUploads: async () => {
        const { repository, logger, analytics, networkAdapter } = adapters;
        const { upload } = get();

        if (upload.isRetrying) {
          return;
        }

        set({ upload: { ...get().upload, isRetrying: true } });

        try {
          const pending = await repository.findPendingUploads();
          const networkState = await networkAdapter.getNetworkState();
          analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RETRY_QUEUE_LENGTH, {
            [ANALYTICS_PARAMS.LENGTH]: pending.length,
          });
          const maxConsecutiveFailures = 3;
          const state = { consecutiveFailures: 0 };

          for (const pendingRecording of pending) {
            if (state.consecutiveFailures >= maxConsecutiveFailures) {
              analytics.track(ANALYTICS_EVENTS.DEAL_MEETING_RETRY_EXHAUSTED, {
                [ANALYTICS_PARAMS.LENGTH]: pending.length,
                [ANALYTICS_PARAMS.NETWORK_TYPE]: networkState.type ?? 'unknown',
                [ANALYTICS_PARAMS.RECORDING_SESSION_ID]: pendingRecording.id,
              });
              break;
            }

            const result = await retryPendingUpload(pendingRecording);
            if (result === 'skipped') {
              continue;
            }

            if (result === 'uploaded') {
              handleSuccessfulUploadedRecording(pendingRecording.id);
              state.consecutiveFailures = 0;

              continue;
            }

            if (result === 'meeting_created') {
              state.consecutiveFailures = 0;

              continue;
            }

            state.consecutiveFailures++;
          }

          await get().loadPendingUploadMeetingIds();
        } catch (e) {
          logger.captureException(e as Error);
        } finally {
          set({ upload: { ...get().upload, isRetrying: false } });
        }
      },

      retryUpload: async (id: string): Promise<boolean> => {
        const { repository, canUpload } = adapters;
        const { upload } = get();

        if (upload.retryingRecordingId) {
          return false;
        }

        const failedRecording = await repository.findById(id);
        if (!failedRecording || !RETRYABLE_UPLOAD_STATUSES.has(failedRecording.uploadStatus)) {
          return false;
        }

        const allowed = await canUpload();
        if (!allowed) {
          return false;
        }

        set({ upload: { ...get().upload, retryingRecordingId: id } });

        try {
          setItemStatus(id, 'uploading');
          const meetingId = failedRecording.meetingId
            ? await uploadFileAndAttach(failedRecording, 'failed', 'retry')
            : await uploadRecording(failedRecording, undefined, 'failed', 'retry');

          if (meetingId) {
            set({
              recording: {
                ...get().recording,
                items: get().recording.items.filter((r) => r.id !== id),
              },
              upload: { ...get().upload, completedAt: Date.now() },
            });

            return true;
          }

          setItemStatus(id, 'failed');

          return false;
        } finally {
          set({ upload: { ...get().upload, retryingRecordingId: null } });
        }
      },

      loadTotalSize: async () => {
        const { fileStorage, logger } = adapters;
        try {
          const size = await fileStorage.getRecordingsDirectorySize();
          set({ upload: { ...get().upload, totalSize: size } });
        } catch (e) {
          logger.captureException(e as Error);
          set({ upload: { ...get().upload, totalSize: 0 } });
        }
      },

      loadPendingUploadMeetingIds: async () => {
        const { repository, logger } = adapters;

        try {
          const [pendingUploadMeetingIds, failedUploadMeetingIds] = await Promise.all([
            repository.findMeetingIdsWithPendingUploads(),
            repository.findMeetingIdsWithFailedUploads(),
          ]);

          set({
            upload: {
              ...get().upload,
              pendingUploadMeetingIds,
              failedUploadMeetingIds,
            },
          });
        } catch (e) {
          logger.captureException(e as Error);
          set({
            upload: {
              ...get().upload,
              pendingUploadMeetingIds: [],
              failedUploadMeetingIds: [],
            },
          });
        }
      },
    };
  });
};
